import {
  Dialog as BaseDialog,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import classNames from 'classnames';
import { ReactNode } from 'react';

type Props = {
  children?: ReactNode;
  show: boolean;
  title: string;
  onClose: () => void;
  danger?: boolean;
  isLoading?: boolean;
};

export const Dialog = ({
  children,
  show,
  title,
  onClose,
  danger,
  isLoading = false,
}: Props) => {
  return (
    <Transition appear show={show}>
      <BaseDialog
        as='div'
        className='fixed inset-0 z-[10000] overflow-hidden'
        // Uncomment below to make clicking on the background close the modal
        // onClose={onClose}
        onClose={() => {}}
        onKeyDown={(e: React.KeyboardEvent) => {
          // Block Backspace/Delete when focus isn't in an editable element
          if (e.key === 'Backspace' || e.key === 'Delete') {
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName;
            const isInputOrTextArea =
              target?.matches?.('input, textarea') ??
              (tag === 'INPUT' || tag === 'TEXTAREA');
            const isSelect = tag === 'SELECT';
            const isEditable =
              target?.isContentEditable === true ||
              isInputOrTextArea ||
              isSelect;

            // Prefer DOM property checks so programmatic state changes are respected.
            // Include selects and buttons for disabled handling.
            let isReadOnlyOrDisabled = false;
            if (isInputOrTextArea) {
              const inputEl = target as
                | HTMLInputElement
                | HTMLTextAreaElement
                | null;
              isReadOnlyOrDisabled = Boolean(
                inputEl?.readOnly || inputEl?.disabled
              );
            } else if (isSelect) {
              const selectEl = target as HTMLSelectElement | null;
              // Check property first; also allow attribute as a fallback
              isReadOnlyOrDisabled = Boolean(
                selectEl?.disabled || target?.hasAttribute?.('disabled')
              );
            } else if (tag === 'BUTTON') {
              const btn = target as HTMLButtonElement | null;
              isReadOnlyOrDisabled = Boolean(
                btn?.disabled || target?.hasAttribute?.('disabled')
              );
            }

            if (!isEditable || isReadOnlyOrDisabled) {
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }}
      >
        <div className='flex h-screen items-center justify-center px-1 text-center'>
          <TransitionChild
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 z-[10000] bg-mask' />
          </TransitionChild>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className='inline-block h-screen align-middle'
            aria-hidden='true'
          >
            &#8203;
          </span>
          <TransitionChild
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <div
              className='z-[10000] my-8 inline-block max-h-[calc(100vh-4rem)] w-[21rem] transform overflow-hidden rounded-lg bg-white transition-all'
              style={{ boxShadow: '0px 3px 6px #00000029' }}
            >
              <DialogTitle
                as='h3'
                className={`${
                  danger ? 'bg-delete' : 'bg-brand'
                } rounded-t-lg px-5 py-3 text-center text-lg font-semibold text-white`}
              >
                <button
                  className={classNames(
                    'x absolute ml-5 mt-3 font-medium transition-all duration-300 hover:opacity-80',
                    {
                      'cursor-default disabled:opacity-50': isLoading,
                    }
                  )}
                  onClick={onClose}
                  disabled={isLoading}
                ></button>
                {/* <span
                  className='x absolute mt-3 ml-5 font-medium transition-all duration-300 hover:opacity-80'
                  onClick={onClose}
                /> */}
                {title}
              </DialogTitle>
              {children}
              {/* <div className='m-8'>
                <p className=' text-copy-alt text-center text-sm font-normal'>
                  Would you like the assignee to be notified of this job via
                  email?
                </p>
              </div>
              <div className='m-4 text-right'>
                <button
                  className='text-future-blue text-xs uppercase'
                  onClick={onClose}
                >
                  Save Only
                </button>
                <button
                  className='bg-brand ml-8 rounded-md p-2 text-xs uppercase text-white shadow'
                  onClick={onClose}
                >
                  Save &amp; Send
                </button>
              </div> */}
            </div>
          </TransitionChild>
        </div>
      </BaseDialog>
    </Transition>
  );
};
